import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Leaf, LayoutDashboard, Pill, MapPin, LineChart, BookOpen, Shield, LogOut, Menu, X, Users, MessagesSquare, Bell, Mail, ChevronLeft, ChevronDown, Settings, MoreHorizontal, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { CrisisButton } from "./CrisisButton";
import { ReminderScheduler } from "./ReminderScheduler";
import { InstallPrompt } from "./InstallPrompt";
import { AccountMenu } from "./AccountMenu";
import { SaveDataInterstitial } from "./SaveDataInterstitial";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

const ITEMS = {
  dashboard: { to: "/app", label: "Dashboard", testid: "dashboard", icon: LayoutDashboard, end: true },
  taper: { to: "/app/taper", label: "Taper Tool", testid: "taper", icon: Pill },
  locator: { to: "/app/locator", label: "Find Support", testid: "locator", icon: MapPin },
  tracker: { to: "/app/tracker", label: "Progress", testid: "tracker", icon: LineChart },
  reminders: { to: "/app/reminders", label: "Reminders", testid: "reminders", icon: Bell },
  buddy: { to: "/app/buddy", label: "Buddy", testid: "buddy", icon: Users },
  community: { to: "/app/community", label: "Community", testid: "community", icon: MessagesSquare },
  learn: { to: "/app/learn", label: "Learn", testid: "education", icon: BookOpen },
  contact: { to: "/contact", label: "Contact", testid: "contact", icon: Mail },
};

// Mobile keeps the full flat list in its original order (unchanged).
const mobileNav = [ITEMS.dashboard, ITEMS.taper, ITEMS.locator, ITEMS.tracker, ITEMS.reminders, ITEMS.buddy, ITEMS.community, ITEMS.learn, ITEMS.contact];
// Desktop: primary items + a "More" dropdown, keeping Contact top-level.
const desktopPrimary = [ITEMS.dashboard, ITEMS.taper, ITEMS.locator, ITEMS.tracker, ITEMS.learn];
const desktopMore = [ITEMS.reminders, ITEMS.buddy, ITEMS.community];

const CRUMBS = {
  "/app/taper": "Taper Tool", "/app/taper/letter": "GP Letter", "/app/locator": "Find Support",
  "/app/tracker": "Progress Tracker", "/app/tracker/summary": "Progress Summary",
  "/app/reminders": "Reminders", "/app/buddy": "Buddy", "/app/community": "Community",
  "/app/learn": "Learn", "/app/admin": "Admin", "/app/settings": "Account Settings", "/app/my-centre": "My Centre",
};
const crumbLabel = (path) => CRUMBS[path] || (Object.entries(CRUMBS).find(([k]) => path.startsWith(k))?.[1]) || "Page";

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isActive = (n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to));
  const linkCls = (active) => `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${active ? "bg-[#4A7c59] text-white" : "text-[#2C363F] hover:bg-[#4A7c59]/10"}`;
  const moreActive = desktopMore.some(isActive);

  const DesktopNav = () => (
    <>
      {desktopPrimary.map((n) => (
        <Link key={n.to} to={n.to} data-testid={`nav-${n.testid}`} className={linkCls(isActive(n))}>
          <n.icon strokeWidth={1.8} className="h-4.5 w-4.5" /> {n.label}
        </Link>
      ))}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="nav-more" className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${moreActive ? "bg-[#4A7c59] text-white" : "text-[#2C363F] hover:bg-[#4A7c59]/10"}`}>
            <MoreHorizontal strokeWidth={1.8} className="h-4.5 w-4.5" /> More <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="rounded-2xl" data-testid="nav-more-content">
          {desktopMore.map((n) => (
            <DropdownMenuItem key={n.to} data-testid={`nav-more-${n.testid}`} onClick={() => navigate(n.to)} className={`cursor-pointer gap-2 ${isActive(n) ? "text-[#4A7c59]" : ""}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Link to={ITEMS.contact.to} data-testid="nav-contact" className={linkCls(isActive(ITEMS.contact))}>
        <Mail strokeWidth={1.8} className="h-4.5 w-4.5" /> {ITEMS.contact.label}
      </Link>
      {user?.role === "admin" && (
        <Link to="/app/admin" data-testid="nav-admin" className={linkCls(loc.pathname.startsWith("/app/admin"))}>
          <Shield strokeWidth={1.8} className="h-4.5 w-4.5" /> Admin
        </Link>
      )}
    </>
  );

  const MobileNav = () => (
    <>
      {mobileNav.map((n) => (
        <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.testid}`} className={linkCls(isActive(n))}>
          <n.icon strokeWidth={1.8} className="h-4.5 w-4.5" /> {n.label}
        </Link>
      ))}
      <Link to="/app/settings" onClick={() => setOpen(false)} data-testid="mobile-nav-settings" className={linkCls(loc.pathname.startsWith("/app/settings"))}>
        <Settings strokeWidth={1.8} className="h-4.5 w-4.5" /> Account Settings
      </Link>
      {user?.manages_centres && (
        <Link to="/app/my-centre" onClick={() => setOpen(false)} data-testid="mobile-nav-mycentre" className={linkCls(loc.pathname.startsWith("/app/my-centre"))}>
          <Building2 strokeWidth={1.8} className="h-4.5 w-4.5" /> My Centre
        </Link>
      )}
      {user?.role === "admin" && (
        <Link to="/app/admin" onClick={() => setOpen(false)} data-testid="mobile-nav-admin" className={linkCls(loc.pathname.startsWith("/app/admin"))}>
          <Shield strokeWidth={1.8} className="h-4.5 w-4.5" /> Admin
        </Link>
      )}
      <button data-testid="logout-btn-mobile" onClick={() => { logout(); navigate("/"); }} className="flex w-full items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium text-muted-foreground"><LogOut className="h-4.5 w-4.5" /> Sign out</button>
    </>
  );

  return (
    <div className="min-h-screen">
      <header className="no-print sticky top-0 z-40 border-b border-slate-200 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#4A7c59] text-white"><Leaf strokeWidth={1.8} className="h-5 w-5" /></div>
            <span className="font-head text-xl font-bold text-[#2C363F]">TaperPath</span>
          </Link>
          <nav className="hidden items-center gap-1 lg:flex"><DesktopNav /></nav>
          <div className="hidden shrink-0 items-center lg:flex">
            <AccountMenu />
          </div>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
        {open && (
          <div className="space-y-1 border-t border-slate-200 px-4 py-3 lg:hidden">
            <MobileNav />
          </div>
        )}
      </header>
      {loc.pathname !== "/app" && loc.pathname.startsWith("/app") && (
        <div className="no-print border-b border-border bg-white/60">
          <nav className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-2.5 text-sm sm:px-6" data-testid="breadcrumb">
            <Link to="/app" data-testid="breadcrumb-dashboard" className="flex items-center gap-1.5 font-medium text-[#4A7c59] hover:underline">
              <ChevronLeft className="h-4 w-4" /> Back to Dashboard
            </Link>
            <span className="text-muted-foreground">/</span>
            <span className="truncate text-muted-foreground">{crumbLabel(loc.pathname)}</span>
          </nav>
        </div>
      )}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">{children}</main>
      <CrisisButton />
      <ReminderScheduler />
      <InstallPrompt />
      <SaveDataInterstitial />
    </div>
  );
};
